import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import {
    siteConfig,
    contactDetails,
    sitemapLinks,
    experienceLinks
} from '@/constants/site'

import { PillButton } from '@/components/generals/pill-button'

const columnHeading = 'text-[12px] font-medium uppercase tracking-[0.16em] text-cream/60'

export const Footer: FC = (): ReactNode => {
    return (
        <footer id='footer' className='relative z-10 bg-ink text-cream'>
            <div className='shell py-16 md:py-20 lg:py-24'>
                <div className='flex flex-col gap-10 border-b border-cream/15 pb-12 lg:flex-row lg:items-end lg:justify-between'>
                    <div data-reveal='up'>
                        <p className='font-heading text-[48px] font-medium leading-none tracking-[-0.04em] text-cream md:text-[72px] lg:text-[88px]'>
                            {siteConfig.name}
                        </p>
                    </div>

                    <div className='max-w-[420px]' data-reveal='up'>
                        <p className='t-h5 text-cream'>
                            Stay, Shop, and Ski in the beauty of Vermont&rsquo;s Green Mountains.
                        </p>
                    </div>
                </div>

                <div
                    className='grid gap-10 py-12 sm:grid-cols-2 lg:grid-cols-4'
                    data-reveal-group
                    data-reveal-stagger='0.09'
                >
                    <div data-reveal='up'>
                        <p className={columnHeading}>
                            {siteConfig.name}
                        </p>

                        <address className='mt-5 not-italic'>
                            {contactDetails.addressLines.map((line) => (
                                <span key={line} className='block font-heading text-[20px] leading-[1.3] text-cream md:text-[24px]'>
                                    {line}
                                </span>
                            ))}
                        </address>

                        <PillButton href='/contact' tone='cream' className='mt-7'>
                            Book now
                        </PillButton>
                    </div>

                    <div data-reveal='up'>
                        <p className={columnHeading}>
                            Contact
                        </p>

                        <ul className='mt-5 flex flex-col gap-3'>
                            <li>
                                <a href={contactDetails.phoneHref} className='text-[16px] text-cream/80 transition-colors hover:text-cream'>
                                    Hotel {contactDetails.phone}
                                </a>
                            </li>

                            <li>
                                <a href={contactDetails.giftShopPhoneHref} className='text-[16px] text-cream/80 transition-colors hover:text-cream'>
                                    Gift Shop {contactDetails.giftShopPhone}
                                </a>
                            </li>

                            <li>
                                <a href={contactDetails.emailHref} className='text-[16px] text-cream/80 transition-colors hover:text-cream'>
                                    {contactDetails.email}
                                </a>
                            </li>

                            <li>
                                <Link href='/contact' className='text-[16px] text-cream/80 transition-colors hover:text-cream'>
                                    Contact us
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div data-reveal='up'>
                        <p className={columnHeading}>
                            Sitemap
                        </p>

                        <ul className='mt-5 flex flex-col gap-2'>
                            {sitemapLinks.map((item) => (
                                <li key={item.label}>
                                    <Link
                                        href={item.href}
                                        className='font-heading text-[22px] leading-[1.25] text-cream/80 transition-colors hover:text-cream md:text-[26px]'
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div data-reveal='up'>
                        <p className={columnHeading}>
                            Experiences
                        </p>

                        <ul className='mt-5 flex flex-col gap-2'>
                            {experienceLinks.map((item) => (
                                <li key={item.label}>
                                    <Link
                                        href={item.href}
                                        className='font-heading text-[22px] leading-[1.25] text-cream/80 transition-colors hover:text-cream md:text-[26px]'
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className='flex flex-col gap-4 border-t border-cream/15 pt-8 sm:flex-row sm:items-center sm:justify-between'>
                    <p className='text-[14px] text-cream/60'>
                        © 2026 {contactDetails.legalName}. All rights reserved.
                    </p>

                    <p className='text-[14px] text-cream/60'>
                        {contactDetails.established}
                    </p>
                </div>
            </div>
        </footer>
    )
}
